import React from 'react';
import { useI18n } from '../i18n';
import { Shield, DollarSign, Network, Database, KeyRound, Server, Check, Plug, TerminalSquare, Lock } from 'lucide-react';

export const Features: React.FC = () => {
  const { t } = useI18n();

  const iconMap: Record<string, React.ReactNode> = {
    dlp: <Shield className="w-6 h-6 text-gold-500" />,
    budget: <DollarSign className="w-6 h-6 text-gold-500" />,
    gateway: <Network className="w-6 h-6 text-gold-500" />,
    rag: <Database className="w-6 h-6 text-gold-500" />,
    iam: <KeyRound className="w-6 h-6 text-gold-500" />,
    deployment: <Server className="w-6 h-6 text-gold-500" />,
    connectors: <Plug className="w-6 h-6 text-gold-500" />,
    sandbox: <TerminalSquare className="w-6 h-6 text-gold-500" />,
    secrets: <Lock className="w-6 h-6 text-gold-500" />,
  };

  return (
    <section id="features" className="py-20 md:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 text-xs font-semibold text-gold-600 dark:text-gold-400 mb-4 uppercase tracking-wider">
            <span>{t.features.badge}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-4">
            {t.features.title}
          </h2>
          <p className="text-base text-slate-600 dark:text-slate-300">
            {t.features.subtitle}
          </p>
        </div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.features.items.map((feature) => (
            <div
              key={feature.id}
              className="glow-card rounded-2xl p-7 flex flex-col justify-between group border-slate-200 dark:border-navy-700/80"
            >
              <div>
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-navy-850 border border-slate-200 dark:border-navy-700 group-hover:border-gold-500/60 flex items-center justify-center transition-colors shadow-sm">
                    {iconMap[feature.id] || <Shield className="w-6 h-6 text-gold-500" />}
                  </div>
                  <span className="text-[11px] font-mono uppercase tracking-wider text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-navy-900 px-2.5 py-1 rounded-full border border-slate-200 dark:border-navy-800 font-medium">
                    {feature.tag}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2.5 group-hover:text-gold-600 dark:group-hover:text-gold-400 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
                  {feature.description}
                </p>
              </div>

              <div className="pt-4 border-t border-slate-200 dark:border-navy-800/80 space-y-2">
                {feature.specs.map((spec, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                    <Check className="w-3.5 h-3.5 text-gold-500 shrink-0" />
                    <span className="font-medium">{spec}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
